import { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { AutoEnrollmentContract } from '@/AutoEnrollment/application'
import {
  AutoEnrollmentDefaultConditionsModel,
  AutoEnrollmentDocumentsModel,
  AutoEnrollmentRepository,
  AutoEnrollmentTypes,
} from '@/AutoEnrollment/domain'
import { edenredProducts } from '@/Shared/domain'
import { useMetaResponseHandler } from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'

import { Step3Form, Step3ProductForm } from './useAutoEnrollmentFormManager'

export type AutoEnrollmentRequiredSign = {
  id: edenredProducts
  signingId: number
  signed: boolean
  mustBeSigned: boolean
}

export interface IUseAutoEnrollmentProducts {
  getDefaultConditionsData: () => Promise<AutoEnrollmentDefaultConditionsModel[]>
  getDocumentsData: (processType: AutoEnrollmentTypes) => Promise<void>
  model: AutoEnrollmentDefaultConditionsModel[] | null
  documents: AutoEnrollmentDocumentsModel | null
  dowloadedDocuments: AutoEnrollmentRequiredSign[]
  setIsDownloaded: (signingId: number) => void
  toggleProductContracted: (
    productId: edenredProducts,
    form: UseFormReturn<Step3Form>,
  ) => void
  isProductDisabled: (productId: edenredProducts, products: Step3ProductForm[]) => boolean
  setDownloadedDocuments: (documents: AutoEnrollmentRequiredSign[]) => void
}

export const useAutoEnrollmentProducts = (
  repository: AutoEnrollmentRepository,
  contractingMode = false,
): IUseAutoEnrollmentProducts => {
  const { startLoading, stopLoading } = useLoader()
  const { handleMetaResponse } = useMetaResponseHandler()

  const [model, setModel] = useState<AutoEnrollmentDefaultConditionsModel[] | null>(null)
  const [documents, setDocuments] = useState<AutoEnrollmentDocumentsModel | null>(null)

  const [dowloadedDocuments, setDownloadedDocuments] = useState<
    AutoEnrollmentRequiredSign[]
  >([])

  const getDefaultConditionsData = async (): Promise<
    AutoEnrollmentDefaultConditionsModel[]
  > => {
    startLoading()

    const response = await AutoEnrollmentContract(repository).getDefaultConditionsData()

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      if (response.data) {
        setModel(response?.data)
        return response.data
      }
    }

    return []
  }

  const getDocumentsData = async (processType: AutoEnrollmentTypes): Promise<void> => {
    startLoading()

    const response =
      await AutoEnrollmentContract(repository).getDocumentsData(processType)

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      if (response.data) {
        setDocuments(response?.data)

        const toSignDocuments = [] as AutoEnrollmentRequiredSign[]

        response.data.documentsForSigning?.forEach(d => {
          if (d.needsSign) {
            toSignDocuments.push({
              id: d.documentProduct,
              signingId: d.signingId,
              signed: false,
              mustBeSigned: true,
            })
          }
        })

        setDownloadedDocuments(toSignDocuments)
      }
    }
  }

  const setIsDownloaded = (signingId: number): void => {
    const updatedDocuments = dowloadedDocuments.slice()

    updatedDocuments.find(d => d.signingId === signingId)!.signed = true

    setDownloadedDocuments(updatedDocuments)
  }

  const toggleProductContracted = (
    productId: edenredProducts,
    form: UseFormReturn<Step3Form>,
  ): void => {
    const product = model?.find(p => p.productCode === productId)

    const currentProducts = form.getValues('products')

    const currentDownloadedDocuments = dowloadedDocuments.slice()

    if (!currentProducts?.some(product => product.productId === productId)) {
      const productStructure: Step3ProductForm = {
        productId: productId,
        productServices: product?.services?.map(s => s.id),
      }

      form.setValue('products', [...currentProducts, productStructure])

      currentDownloadedDocuments.find(d => d.id === productId)!.mustBeSigned = true
    } else {
      form.setValue(
        'products',
        currentProducts?.filter(
          (product: Step3ProductForm) => product.productId !== productId,
        ),
      )
      currentDownloadedDocuments.find(d => d.id === productId)!.mustBeSigned = false
    }

    setDownloadedDocuments(currentDownloadedDocuments)
  }

  const isProductDisabled = (
    productId: edenredProducts,
    products: Step3ProductForm[],
  ): boolean => {
    const optionalHiring =
      model?.find(m => m.productCode === productId)?.productHiringOptional || false

    return optionalHiring && !products.some(product => product.productId === productId)
  }

  useEffect(() => {
    if (!contractingMode && model && documents) {
      const currentDownloadedDocuments = dowloadedDocuments.slice()
      currentDownloadedDocuments.forEach(d => {
        const associatedModelProduct = model.find(m => m.productCode === d.id)

        if (associatedModelProduct?.productHiringOptional) {
          d.mustBeSigned = false
        }
      })
      setDownloadedDocuments(currentDownloadedDocuments)
    }
  }, [documents])

  return {
    getDefaultConditionsData,
    getDocumentsData,
    model,
    documents,
    dowloadedDocuments,
    setIsDownloaded,
    toggleProductContracted,
    isProductDisabled,
    setDownloadedDocuments,
  }
}
